import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MessageSquareText, X, Volume2, VolumeX, Maximize2 } from "lucide-react";
import { http } from "@/lib/api";
import { chime, isMuted, setMuted } from "@/lib/chime";
import { useProfile } from "@/context/ProfileContext";
import { Conversation } from "@/components/messages/Conversation";

export const MuteToggle = ({ testId = "chat-mute-toggle", className = "" }) => {
  const [m, setM] = useState(isMuted());
  useEffect(() => { const h = () => setM(isMuted()); window.addEventListener("cl-chat-mute", h); return () => window.removeEventListener("cl-chat-mute", h); }, []);
  return (
    <button type="button" onClick={() => setMuted(!m)} aria-label={m ? "Turn chat sounds on" : "Mute chat sounds"} title={m ? "Sounds off" : "Sounds on"} data-testid={testId} aria-pressed={m}
      className={`p-2 rounded-full transition-colors hover:bg-[#F1EADB] text-[#2C4A3E] ${className}`}>{m ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}</button>
  );
};

const MiniList = ({ threads, onPick }) => (
  <ul className="flex-1 overflow-y-auto p-2 space-y-1" data-testid="mini-chat-list">
    {!threads.length && <li className="p-6 text-sm text-stone-400 text-center">No chats yet.</li>}
    {threads.map((t) => (
      <li key={t.id}><button onClick={() => onPick(t.id)} data-testid={`mini-thread-${t.id}`} className="w-full text-left px-3 py-2.5 rounded-2xl hover:bg-[#F1EADB] transition-colors flex items-center gap-2">
        <span className="flex-1 min-w-0"><span className="block text-sm font-medium text-[#2C4A3E] truncate">{t.name}</span><span className="block text-xs text-stone-500 truncate">{t.last_text || "Say hello!"}</span></span>
        {t.unread > 0 && <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-[#C87D53] text-white text-[11px] grid place-items-center">{t.unread}</span>}
      </button></li>))}
  </ul>
);

const MiniWindow = ({ onClose, tid, setTid, emoji }) => {
  const [threads, setThreads] = useState([]);
  const nav = useNavigate();
  const load = () => http.get("/coach-chat/threads").then((r) => setThreads(r.data.threads)).catch(() => {});
  useEffect(() => { load(); }, []);
  return (
    <div className="fixed bottom-24 left-4 sm:left-6 z-50 w-[calc(100vw-2rem)] sm:w-[380px] h-[min(560px,70vh)] rounded-[1.75rem] bg-[#FDFBF7] border border-[#E6DEC3] shadow-2xl flex flex-col overflow-hidden bubble-in" data-testid="mini-chat">
      <div className="px-4 py-3 border-b border-[#E6DEC3] flex items-center gap-1">
        <p className="font-display text-lg text-[#2C4A3E] flex-1">Messages</p>
        <MuteToggle testId="mini-chat-mute" />
        <button onClick={() => { onClose(); nav(tid ? `/messages?thread=${tid}` : "/messages"); }} aria-label="Open full Messages" data-testid="mini-chat-expand" className="p-2 rounded-full hover:bg-[#F1EADB] text-[#2C4A3E]"><Maximize2 className="w-4 h-4" /></button>
        <button onClick={onClose} aria-label="Close chat" data-testid="mini-chat-close" className="p-2 rounded-full hover:bg-[#F1EADB] text-[#2C4A3E]"><X className="w-4 h-4" /></button>
      </div>
      <div className="flex-1 min-h-0 flex flex-col">
        {tid ? <Conversation tid={tid} emoji={emoji} onBack={() => { setTid(null); load(); }} onSeen={() => {}} mini /> : <MiniList threads={threads} onPick={setTid} />}
      </div>
    </div>
  );
};

export const ChatBubble = () => {
  const { profile } = useProfile();
  const loc = useLocation();
  const [n, setN] = useState(0);
  const [pulse, setPulse] = useState(0);
  const [open, setOpen] = useState(false);
  const [tid, setTid] = useState(null);
  const [emoji, setEmoji] = useState([]);
  const prev = useRef(null);
  const latest = useRef(null);
  const enabled = profile && ["student", "parent", "teacher"].includes(profile.role);
  useEffect(() => {
    if (!enabled) return;
    const load = () => http.get("/coach-chat/unread").then((r) => {
      const { unread, latest: l } = r.data;
      if (prev.current !== null && unread > prev.current && l?.id !== latest.current && l?.thread_id !== window.__clOpenThread) { chime(1); setPulse((p) => p + 1); }
      prev.current = unread; latest.current = l?.id; setN(unread);
    }).catch(() => {});
    load();
    const t = setInterval(load, 10000);
    window.addEventListener("coach-chat-seen", load);
    return () => { clearInterval(t); window.removeEventListener("coach-chat-seen", load); };
  }, [enabled]);
  useEffect(() => { if (open && !emoji.length) http.get("/coach-chat/threads").then((r) => setEmoji(r.data.emoji)).catch(() => {}); }, [open, emoji.length]);
  if (!enabled || loc.pathname.startsWith("/messages") || loc.pathname.startsWith("/live/")) return null;
  return (
    <>
      {open && <MiniWindow onClose={() => { setOpen(false); setTid(null); }} tid={tid} setTid={setTid} emoji={emoji} />}
      <button key={pulse} onClick={() => { setOpen(!open); setTid(null); }} data-testid="chat-bubble" aria-label={`Messages${n ? `, ${n} unread` : ""}`}
        className={`fixed bottom-6 left-4 sm:left-6 z-50 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#C87D53] text-white shadow-xl grid place-items-center hover:scale-105 transition-transform ${pulse ? "chat-pulse" : ""}`}>
        {open ? <X className="w-5 h-5" /> : <MessageSquareText className="w-5 h-5 sm:w-6 sm:h-6" />}
        {n > 0 && !open && <span data-testid="chat-bubble-unread" className="absolute -top-1 -right-1 min-w-[22px] h-[22px] px-1 rounded-full bg-[#2C4A3E] text-white text-[11px] grid place-items-center ring-2 ring-white">{n > 99 ? "99+" : n}</span>}
      </button>
    </>
  );
};
